import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { HelpCircle, ArrowLeft, Ticket } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Images from "../../utils/Images";
import { mediaEventOptions } from "../../data/mediaEventChoiceData";
import OptionCard from "../../components/event_media/OptionCard";
import HeroSlider from "../../components/event_media/HeroSlider";
import AuthenticatedFooter from "../../components/landing/AuthenticatedFooter";

export default function EventMediaSales() {
  const navigate = useNavigate();
  const [helperOpen, setHelperOpen] = useState(false);
  const [toast, setToast] = useState("");
  

  const heroImages = [
    Images.hero_img_web_three,
    Images.hero_img_web_four,
  ].filter(Boolean);
  const heroSlides = heroImages.length ? heroImages : [Images.hero_img_web_one];

  // Split conference-trade-mission into two cards: Conference → /conference, Trade Mission → /trade-mission
  const transformedOptions = mediaEventOptions.flatMap((opt) => {
    if (opt.key === "conference-trade-mission") {
      return [
        {
          key: "conference",
          title: "Conference",
          subtitle: "Connect, learn, and engage",
          image: opt.image,
          link: "/event-media-sales/conference",
        },
        {
          key: "trade-mission",
          title: "Trade Mission",
          subtitle: "Explore markets, build partnerships",
          image: Images.trade_mission,
          link: "/event-media-sales/trade-mission",
        },
      ];
    }
    return [opt];
  });

  const handleCardClick = (opt) => {
    if (opt.comingSoon) {
      setToast("This item is coming soon");
      setTimeout(() => setToast(""), 1800);
      return;
    }
    if (opt.link) {
      navigate(opt.link);
    } else {
      setToast("Feature coming soon");
      setTimeout(() => setToast(""), 1800);
    }
  };

  return (
    <div className="bg-white">
      <HeroSlider
        images={heroSlides}
        showBackButton={true}
        backLink="/dashboard"
        backLabel="Back to Dashboard"
      />

      <section className="px-4 py-12 bg-gradient-to-b from-gray-50 to-white sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-center mb-8">
            <div className="min-w-0">
              <h2 className="text-2xl font-bold text-gray-900 sm:text-3xl">
                Event & Media Sales
              </h2>
              <div className="mt-2 w-32 h-1 bg-emerald-500 rounded" />
            </div>
            <div className="flex flex-wrap gap-2">
              <motion.button
                onClick={() => navigate("/event-media-sales/purchased-events")}
                whileHover={{ scale: 1.03, y: -1 }}
                whileTap={{ scale: 0.97 }}
                className="inline-flex gap-2 items-center px-4 py-2 text-sm font-semibold text-white bg-violet-600 rounded-lg border border-violet-500 shadow-sm transition-colors duration-200 hover:bg-violet-700"
              >
                <Ticket size={16} />
                My purchased events
              </motion.button>
              <motion.button
                onClick={() => navigate("/dashboard")}
                whileHover={{ scale: 1.03, y: -1 }}
                whileTap={{ scale: 0.97 }}
                className="inline-flex gap-2 items-center px-4 py-2 text-sm font-semibold text-emerald-700 bg-emerald-50 rounded-lg border border-emerald-200 shadow-sm transition-colors duration-200 hover:bg-emerald-100 hover:border-emerald-300"
              >
                <ArrowLeft size={16} />
                Back to Dashboard
              </motion.button>
            </div>
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.35 }}
              className="grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-3 lg:grid-cols-4"
            >
              {transformedOptions.map((opt, index) => (
                <motion.div
                  key={`${opt.key}-${index}`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="w-full"
                >
                  <OptionCard
                    option={opt}
                    onClick={() => handleCardClick(opt)}
                  />
                </motion.div>
              ))}
            </motion.div>
          </AnimatePresence>

          <motion.button
            onClick={() => setHelperOpen((v) => !v)}
            className="inline-flex fixed bottom-4 left-4 z-30 justify-center items-center w-12 h-12 text-white bg-gray-900 rounded-full shadow-lg sm:left-6 sm:bottom-6"
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
          >
            <HelpCircle size={20} />
          </motion.button>

          <AnimatePresence>
            {helperOpen && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 10 }}
                transition={{ duration: 0.25 }}
                className="fixed left-4 sm:left-6 bottom-16 sm:bottom-20 z-30 p-3 sm:p-4 w-80 sm:w-72 max-w-[calc(100vw-2rem)] sm:max-w-none text-xs sm:text-sm text-gray-700 bg-white rounded-xl border border-gray-200 shadow-xl"
              >
                <p className="mb-2 font-semibold text-gray-900">How it works</p>
                <ul className="pl-4 space-y-1 list-disc sm:pl-5">
                  <li>Choose an option to explore event and media sales.</li>
                  <li>Items marked as Coming soon are not yet available.</li>
                  <li>More services can be added as they go live.</li>
                </ul>
              </motion.div>
            )}
          </AnimatePresence>

          <AnimatePresence>
            {toast && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 8 }}
                className="fixed bottom-6 left-1/2 z-30 px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg shadow-lg -translate-x-1/2"
              >
                {toast}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>
            <AuthenticatedFooter />
    </div>
  );
}
